import { createFileRoute, Link } from "@tanstack/react-router";
import { useReveal } from "@/hooks/use-reveal";
import { TldrBox } from "@/components/tldr-box";
import { SETUP_PHOTO } from "@/lib/responsive-images";
import { PageHero } from "@/components/page-hero";
import { SeoAccordion } from "@/components/seo-accordion";
import { LOCATION_SECTIONS } from "@/data/seo-blocks";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
const URL = "https://dj-val.fr/location-sono-mariage";

const PACKS = [
  {
    name: "Sono Mariage 50 invités",
    price: "à partir de 290 €",
    bullets: [
      "2 enceintes actives",
      "2 pieds d'enceinte",
      "1 micro HF (cérémonie / discours)",
      "Une barre de lumière 4 en 1",
      "Câblage + table de mixage",
    ],
  },
  {
    name: "Sono Mariage 80 invités",
    price: "à partir de 290 €",
    bullets: [
      "2 enceintes actives Electro-Voice EKX-15P",
      "1 caisson de basses 1000 W",
      "1 micro HF Shure (cérémonie / discours)",
      "Câblage + table de mixage",
      "Livraison & installation incluses ≤ 80 km",
    ],
  },
  {
    name: "Sono + Lumière Mariage 150 invités",
    price: "à partir de 490 €",
    bullets: [
      "Sono 4 points (cérémonie + soirée)",
      "4 PAR LED + 2 lyres beam Chauvet",
      "Machine à fumée légère pour ouverture de bal",
      "2 micros HF Shure",
      "Contrôleur DMX + retour scène",
    ],
    highlight: true,
  },
  {
    name: "Pack Prestige Mariage 200+ invités",
    price: "à partir de 890 €",
    bullets: [
      "Line-array RCF HDL multi-zones",
      "Lyres LED + lasers + étincelles froides",
      "Régie Pioneer CDJ-3000 · DJM-A9 fournie",
      "Photobooth premium en option",
      "Technicien sur site (option)",
    ],
  },
];

const FAQ = [
  {
    q: "Combien coûte la location d'une sono pour un mariage dans le Vaucluse ?",
    a: "À partir de 290 € pour un mariage 50 invités (2 enceintes + barre de lumière + micro HF). Comptez 290 € pour 80 invités avec caisson, 490 € pour 150 invités avec mise en lumière, et 890 € et + pour 200 invités avec line-array et lyres. Tous les tarifs incluent la livraison-installation dans un rayon de 80 km autour de Cavaillon.",
  },
  {
    q: "La sono est-elle livrée et installée ?",
    a: "Oui. DJ Val livre, installe, cale le son et démonte le matériel après la soirée. Aucun frais de déplacement ≤ 80 km autour de Cavaillon (Avignon, L'Isle-sur-la-Sorgue, Gordes, Lourmarin…). Forfait kilométrique transparent au-delà (0,60 €/km).",
  },
  {
    q: "Peut-on louer uniquement la sono cérémonie laïque ?",
    a: "Oui — pack cérémonie dédié à partir de 190 € : 2 enceintes sur pieds, 2 micros HF (officiant + témoins), platine pour bandes-son, autonomie 4 h. Livré, installé et récupéré sur site.",
  },
  {
    q: "Faut-il un technicien sur place pendant la soirée ?",
    a: "Non, le matériel est calé pour fonctionner en autonomie (vos amis lancent la playlist via Bluetooth ou USB). Option « technicien sono » à 250 € si vous voulez quelqu'un sur place pour la soirée. Sinon, basculez sur la prestation DJ complète.",
  },
  {
    q: "Quelle différence avec un pack DJ mariage complet ?",
    a: "La location sono ne comprend pas le DJ : vous gérez la playlist vous-mêmes. Si vous voulez un DJ pro qui anime la soirée (entrée des mariés, ouverture de bal, animations), regardez le Pack DJ Mariage sono + lumière à partir de 1 490 €.",
  },
];

export const Route = createFileRoute("/location-sono-mariage")({
  head: () => ({
    meta: [
      { title: "Location sono mariage dès 290 € | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Location de sono mariage dans le Vaucluse, Avignon et Cavaillon. Pack cérémonie + soirée dès 290 €, livraison et installation incluses. Devis gratuit en 24 h.")
      },
      { property: "og:title", content: "Location Sono Mariage Vaucluse dès 290 € | DJ Val" },
      { property: "og:description", content: metaDesc("Sono mariage pro RCF / Electro-Voice livrée installée — Avignon, Cavaillon, Aix, Arles.") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "product" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Location sono mariage Vaucluse",
          description:
            "Pack location sono + éclairage pour mariage en Provence : enceintes pro, micros HF, caisson, livraison-installation incluses.",
          brand: { "@type": "Brand", name: "DJ Val" },
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "EUR",
            lowPrice: "290",
            highPrice: "890",
            offerCount: 4,
            availability: "https://schema.org/InStock",
            areaServed: ["Vaucluse", "Bouches-du-Rhône", "Gard", "Avignon", "Cavaillon"],
            url: URL,
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              url: "https://dj-val.fr/conditions-de-location",
              applicableCountry: "FR",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "EUR" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "FR" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
              },
            },
          },
        }),

      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
            { "@type": "ListItem", position: 2, name: "Location matériel", item: "https://dj-val.fr/location-materiel" },
            { "@type": "ListItem", position: 3, name: "Location sono mariage", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={SETUP_PHOTO.src}
        eyebrow="Location matériel · Vaucluse · Avignon · Cavaillon"
        title={<>Location sono <span className="italic">mariage</span></>}
        subtitle="Pack sono + éclairage pour mariage, livré et installé chez vous. À partir de 290 €. 1000+ locations en Provence depuis 20 ans."
        primary={{ label: "Devis gratuit sous 24 h", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:+33767908667" }}
      />
      <ReviewsProof />


      <section className="mx-auto max-w-6xl px-4 py-16">
        <TldrBox
          title="TL;DR — Location sono mariage Vaucluse"
          items={[
            { label: "Formules", value: "290 € (50/80 inv.) · 490 € (150 inv.) · 890 € (200+ inv.)" },
            { label: "Livraison", value: "Incluse ≤ 80 km autour de Cavaillon" },
            { label: "Matériel", value: "RCF, Electro-Voice EKX-15P, Pioneer, Shure, Chauvet" },
            { label: "Zone", value: "Avignon, Cavaillon, Aix, Arles, Nîmes, Salon, Carpentras" },
          ]}
        />
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="font-serif text-3xl md:text-4xl text-center mb-12">4 packs sono mariage</h2>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {PACKS.map((p) => (
            <div
              key={p.name}
              className={`rounded-2xl border bg-white p-8 ${p.highlight ? "border-[hsl(var(--gold))] shadow-xl scale-[1.02]" : "border-stone-200"}`}
            >
              {p.highlight && (
                <span className="inline-block mb-3 rounded-full bg-[hsl(var(--gold))]/15 px-3 py-1 text-xs font-medium text-[hsl(var(--gold))]">
                  Le plus demandé
                </span>
              )}
              <h3 className="font-serif text-2xl mb-2">{p.name}</h3>
              <p className="text-3xl font-light text-[hsl(var(--gold))] mb-6">{p.price}</p>
              <ul className="space-y-2 text-sm text-stone-700 mb-6">
                {p.bullets.map((b) => (
                  <li key={b} className="flex gap-2"><span className="text-[hsl(var(--gold))]">✓</span> {b}</li>
                ))}
              </ul>
              <Link to="/contactez-nous" className="block rounded-full bg-black px-6 py-3 text-center text-sm text-white hover:bg-black/80">
                Réserver ce pack
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="font-serif text-3xl md:text-4xl text-center mb-10">Questions fréquentes</h2>
          <div className="space-y-4">
            {FAQ.map((f) => (
              <details key={f.q} className="group rounded-xl border border-stone-200 bg-[hsl(var(--ivory))] p-5">
                <summary className="cursor-pointer font-medium text-stone-900">{f.q}</summary>
                <p className="mt-3 text-stone-700 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 text-center">
        <h2 className="font-serif text-3xl mb-4">Vous préférez un DJ pro qui anime ?</h2>
        <p className="text-stone-700 mb-6 max-w-2xl mx-auto">
          Découvrez le Pack DJ Mariage sono + lumière complet (animation, ouverture de bal, étincelles froides) à partir de 690 €.
          Pour comparer les budgets, lisez le guide des{" "}
          <Link
            to="/actualites/$slug"
            params={{ slug: "prix-dj-mariage-provence-guide-complet-2026" }}
            className="underline underline-offset-4 text-[hsl(var(--gold))]"
          >
            tarifs d'un DJ de mariage en Provence
          </Link>
          .
        </p>
        <Link to="/dj-mariage/pack-sono-lumiere" className="inline-block rounded-full bg-[hsl(var(--gold))] px-8 py-3 text-sm font-medium text-black hover:opacity-90">
          Voir le pack DJ mariage complet →
        </Link>
      </section>


      <SeoAccordion
        eyebrow="Location matériel · Aller plus loin"
        title={<>Location sono : <span className="italic">vos questions</span></>}
        intro="Tarifs, livraison, caution, marques utilisées, garanties : tout ce qu'il faut savoir avant de louer."
        sections={LOCATION_SECTIONS}
      />
    </div>
  );
}
